if (!customElements.get('sticky-atc')) {
  customElements.define(
    'sticky-atc',
    class StickyATC extends HTMLElement {
      constructor () {
        super();
        this.mode = this.getAttribute('data-mode');
        this.offsetVarName = '--header-height';
        this.imageContainer = this.querySelector('[data-type="image"]');
        this.productID = this.hasAttribute('data-product-id') ? this.getAttribute('data-product-id') : null;
        this.productHandle = this.hasAttribute('data-product-handle') ? this.getAttribute('data-product-handle') : null;
        this.template = this.getAttribute('data-template');
        this.delimiter = document.querySelector(`div:has(>#ProductSection-${this.productID}) .shipping-count-down`);
        if (!this.delimiter) {
          this.delimiter = document.querySelector(`div:has(>#ProductSection-${this.productID}) + div`);
        }
        this.productForm = document.querySelector(`#AddToCartForm-${this.productID}`);
        this.productQuantityField = this.productForm?.querySelector('[name=quantity]') || null;
        this.variantID = this.productForm?.querySelector('[name=id]')?.value || null;
        this.button = this.querySelector('button');
        const locales = this.querySelector(`#sticky-locales`)?.textContent;
        const unavailableForSaleVariantIds = this.getAttribute('data-unavailable-for-sale-variant-ids');
        this.unavailableForSaleVariantIds = new Set();

        this.intersectionObserver = new IntersectionObserver(this.intersectionCallback.bind(this), { threshold: 1 });
        this.intersection = false;

        this.lastScrollY = window.scrollY;
        this.scrollingDown = false;
        this.menuOpened = false;

        this.isMobile = window.innerWidth < 750;
        this.updateIsMobile = () => {
          const newValue = window.innerWidth < 750;
          if (newValue !== this.isMobile) {
            this.isMobile = newValue;
            this.toggleMobileMode();
          }
        };
        this.toggleMobileMode();
        window.addEventListener('resize', this.updateIsMobile);

        this.scrollHandler = () => {
          const currentY = window.scrollY;
          this.scrollingDown = currentY > this.lastScrollY;
          if (!this.scrollingDown && currentY > 0) {
            this.classList.add('scrolling-up');
          } else {
            this.classList.remove('scrolling-up');
          }
          this.lastScrollY = currentY;
        };

        window.addEventListener('scroll', this.scrollHandler);

        try {
          if (!locales) {
            throw new Error('Locales not found');
          }
          this.locales = JSON.parse(locales);
        } catch (e) {
          console.error(e.message);
          this.locales = {};
        }

        if (unavailableForSaleVariantIds) {
          try {
            const parsedIds = JSON.parse(unavailableForSaleVariantIds);
            if (Array.isArray(parsedIds)) {
              this.unavailableForSaleVariantIds = new Set(parsedIds.map((id) => Number(id)));
            }
          } catch (e) {
            console.error('Failed to parse unavailable_for_sale variant ids');
          }
        }
      }

      intersectionCallback (entries) {
        entries.forEach((entry) => {
          if (!entry.isIntersecting && this.scrollingDown && !this.menuOpened) {
            this.classList.add('opened');
          } else if (entry.isIntersecting && !this.scrollingDown) {
            this.classList.remove('opened');
          }
        });
      }

      render (item) {
        const currentVariantID = item.getAttribute('data-variant-id');
        if (currentVariantID === this.variantID) {
          item.style.display = 'block';
        } else {
          item.style.display = 'none';
        }
      }

      resolveGotoTarget () {
        if (this.productForm) {
          const el = document.querySelector('.advanced-product-template');
          if (el) {
            return el;
          }
          return this.productForm;
        }
        return null;
      }

      parseCssLength (val) {
        if (!val) return NaN;
        const v = String(val).trim();
        if (v.endsWith('px')) return parseFloat(v);
        if (v.endsWith('rem')) {
          const rootFont = parseFloat(getComputedStyle(document.documentElement).fontSize || '16');
          return parseFloat(v) * rootFont;
        }
        const num = parseFloat(v);
        return isNaN(num) ? NaN : num;
      }

      getCssVarPx (el, varName) {
        const raw = getComputedStyle(el).getPropertyValue(varName);
        return this.parseCssLength(raw);
      }

      getHeaderOffset () {
        let px = this.getCssVarPx(document.documentElement, this.offsetVarName);
        if (!isNaN(px) && px > 0) return px;
      }

      gotoTarget () {
        const target = this.resolveGotoTarget();
        if (!target) return;
        const offset = this.getHeaderOffset();
        const prefersNoMotion = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
        const behavior = prefersNoMotion ? 'auto' : 'smooth';
        const top = target.getBoundingClientRect().top + window.scrollY - offset;
        try {
          window.scrollTo({ top, behavior });
        } catch {
          window.scrollTo(0, top);
        }
      }

      productFormChangeHandle (e) {
        this.variantID = this.productForm.querySelector('[name=id]')?.value * 1;
        const currentVariant = this.product.variants.find((variant) => variant.id === this.variantID);
        if (currentVariant) {
          if (currentVariant.featured_image?.src) {
            const sgSrc = currentVariant.featured_image.src;
            this.imageContainer.querySelector('img').src = `${sgSrc}${sgSrc.includes('?') ? '&' : '?'}width=160`;
          }
          this.setButton(this.isVariantAvailable(currentVariant));
        } else {
          this.setButton(false);
        }
      }

      isVariantAvailable (variant) {
        if (!variant) {
          return false;
        }
        if (this.unavailableForSaleVariantIds.has(Number(variant.id))) {
          return false;
        }
        return !!variant.available;
      }

      setButton (available) {
        let buttonText;
        if (!available) {
          this.button.setAttribute('disabled', 'disabled');
          buttonText = this.locales?.soldOut ?? null;
          if (this.template.indexOf('preorder') !== -1) {
            buttonText = this.locales?.preOrder ?? null;
          }
        } else {
          buttonText = this.locales?.addToCart ?? null;
          this.button.removeAttribute('disabled');
        }
        if (buttonText !== null) {
          this.button.querySelector('span:first-child').innerHTML = buttonText;
        }
      }

      buttonHandle (e) {
        if (this.mode === 'goto') {
          e?.preventDefault?.();
          this.gotoTarget();
        } else {
          this.productForm?.dispatchEvent(new Event('submit'));
        }
      }

      productQuantityChangeHandle (e) {
        this.productForm.dispatchEvent(new Event('change'));
      }

      async getProduct () {
        try {
          const response = await fetch(window.Shopify.routes.root + `products/${this.productHandle}.js`);
          if (!response.ok) {
            const error = await response.json();
            throw new Error(error?.message ?? 'Unexpected error');
          }
          return await response.json();
        } catch (error) {
          throw error;
        }
      }

      async connectedCallback () {
        if (this.productForm && this.variantID !== null && this.productQuantityField && this.productHandle) {
          this.product = await this.getProduct();
          this.product.variants = this.product.variants.map((variant) => {
            if (this.unavailableForSaleVariantIds.has(Number(variant.id))) {
              return {
                ...variant,
                available: false,
              };
            }
            return variant;
          });
          this.productForm.addEventListener('change', this.productFormChangeHandle.bind(this));
          if (this.productQuantityField) {
            this.productQuantityField.addEventListener('quantityChanged', this.productQuantityChangeHandle.bind(this));
          }
        }

        if (this.delimiter) {
          this.intersectionObserver.observe(this.delimiter);
          if (this.productForm) {
            this.button.addEventListener('click', this.buttonHandle.bind(this));
          }
        }
      }

      menuOpenHandler () {
        this.classList.remove('opened');
        this.menuOpened = true;
      }

      menuCloseHandler () {
        this.menuOpened = false;
        if (this.delimiter) {
          const delimiterBottom = this.delimiter.getBoundingClientRect().bottom;
          const isBelow = delimiterBottom < 0;

          if (isBelow) {
            this.classList.add('opened');
          } else {
            this.classList.remove('opened');
          }
        }
      }

      toggleMobileMode () {
        const headerWrapper = document.querySelector('.header-wrapper');
        if (headerWrapper) {
          if (this.isMobile) {
            this.menuOpened = false;
            headerWrapper.removeEventListener('menu-open', this.menuOpenHandler.bind(this));
            headerWrapper.removeEventListener('menu-close', this.menuCloseHandler.bind(this));
          } else {
            headerWrapper.addEventListener('menu-open', this.menuOpenHandler.bind(this));
            headerWrapper.addEventListener('menu-close', this.menuCloseHandler.bind(this));
          }
        }
      }

      disconnectedCallback () {
        window.removeEventListener('scroll', this.scrollHandler);
        window.removeEventListener('resize', this.updateIsMobile);
        if (this.productForm) {
          this.productForm.removeEventListener('change', this.productFormChangeHandle.bind(this));
        }
        if (this.delimiter) {
          this.intersectionObserver.unobserve(this.delimiter);
        }
        if (this.productQuantityField) {
          this.productQuantityField.removeEventListener('quantityChanged', this.productQuantityChangeHandle.bind(this));
        }
      }
    },
  );
}
